import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAppSelector } from '../store/hooks';
import { API_ORIGIN } from '../config/api';

/**
 * ProfileCompletion Component
 * Collects user profile information (profession, state, country) after initial authentication
 */
const ProfileCompletion = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);

  // Form state
  const [profession, setProfession] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    profession?: string;
    state?: string;
    country?: string;
  }>({});

  /**
   * Validate form fields
   * @returns true if form is valid, false otherwise
   */
  const validateForm = (): boolean => {
    const errors: { profession?: string; state?: string; country?: string } = {};

    if (!profession.trim()) {
      errors.profession = 'Profession is required';
    }

    if (!state.trim()) {
      errors.state = 'State is required';
    }

    if (!country.trim()) {
      errors.country = 'Country is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setError(null);
    setValidationErrors({});

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Check if user is available
    if (!user || !user.id) {
      setError('User information not available. Please log in again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const csrfMatch = document.cookie.match(/(^|;)\s*_csrf_token\s*=\s*([^;]+)/);
      if (csrfMatch && csrfMatch[2]) {
        headers['X-CSRF-Token'] = csrfMatch[2];
      }

      const response = await fetch(`${API_ORIGIN}/api/profile`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          userId: user.id,
          profession: profession.trim(),
          state: state.trim(),
          country: country.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors from backend
        if (data.errors && Array.isArray(data.errors)) {
          setError(data.errors.join(', '));
        } else {
          setError(data.message || 'Failed to save profile. Please try again.');
        }
        return;
      }

      if (data.success) {
        // Profile saved successfully, navigate to dashboard
        navigate('/dashboard');
      } else {
        setError('Failed to save profile. Please try again.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Network error. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Clear error message
   */
  const handleClearError = () => {
    setError(null);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
          }}
        >
          {/* Title */}
          <Typography
            component="h1"
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              marginBottom: 1,
              textAlign: 'center',
            }}
          >
            Complete Your Profile
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              marginBottom: 3,
              textAlign: 'center',
            }}
          >
            Welcome{user?.email ? `, ${user.email}` : ''}! Please provide the following information
            to complete your profile and access the platform.
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              onClose={handleClearError}
              sx={{ width: '100%', marginBottom: 3 }}
            >
              {error}
            </Alert>
          )}

          {/* Profile Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ width: '100%', maxWidth: '400px' }}
            noValidate
          >
            {/* Profession Field */}
            <TextField
              fullWidth
              label="Profession"
              variant="outlined"
              value={profession}
              onChange={(e) => setProfession(e.target.value)}
              error={!!validationErrors.profession}
              helperText={validationErrors.profession}
              disabled={isSubmitting}
              required
              sx={{ marginBottom: 2 }}
              placeholder="e.g., GIS Analyst, Urban Planner, Geographer"
            />

            {/* State Field */}
            <TextField
              fullWidth
              label="State"
              variant="outlined"
              value={state}
              onChange={(e) => setState(e.target.value)}
              error={!!validationErrors.state}
              helperText={validationErrors.state}
              disabled={isSubmitting}
              required
              sx={{ marginBottom: 2 }}
              placeholder="e.g., California, New York"
            />

            {/* Country Field */}
            <TextField
              fullWidth
              label="Country"
              variant="outlined"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              error={!!validationErrors.country}
              helperText={validationErrors.country}
              disabled={isSubmitting}
              required
              sx={{ marginBottom: 3 }}
              placeholder="e.g., United States, Canada"
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isSubmitting}
              sx={{
                padding: '12px 24px',
                fontSize: '16px',
                textTransform: 'none',
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ marginRight: 1 }} />
                  Saving...
                </>
              ) : (
                'Complete Profile'
              )}
            </Button>

            <Button
              type="button"
              fullWidth
              disabled={isSubmitting}
              onClick={() => navigate('/dashboard')}
              sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              Skip for now — open WebGIS
            </Button>
          </Box>

          {/* Additional Info */}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              marginTop: 3,
              textAlign: 'center',
              maxWidth: '400px',
            }}
          >
            You can finish your profile later from the account menu. The full map workspace is ready
            either way.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default ProfileCompletion;
